import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { PillButton } from '../PillButton';
import { ProductVisual } from '../products/ProductVisual';
import { SiblingGuessRow } from '../SiblingGuessRow';
import { SiblingFigure } from '../characters/SiblingFigure';
import type { FilteknProduct, SiblingRole } from '../../types/snak';
interface ProductScreenProps {
  product: FilteknProduct;
  position: number;
  total: number;
  onNext: () => void;
}
export function ProductScreen({
  product,
  position,
  total,
  onNext
}: ProductScreenProps) {
  const [guess, setGuess] = useState<SiblingRole | null>(null);
  const matched = guess === product.answer;
  const isLast = position === total;
  return <div className="flex flex-1 flex-col py-3" data-id="element-354" data-mp="t87xdhgefqsa">
      <Reveal data-id="element-355" data-mp="y8m14nayweki">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-356" data-mp="y93vtfhzekqb">
          Introduction {position} of {total}
        </span>
      </Reveal>
      <Reveal delay={0.06} data-id="element-357" data-mp="q25zxbkbejk8">
        <h2 className="mt-2 font-heading text-[30px] leading-[1.02] tracking-[-0.02em] text-white" data-id="element-358" data-mp="q3h3ba9ulpat">
          {product.headline}
        </h2>
      </Reveal>

      <Reveal delay={0.14} className="mt-5" data-id="element-359" data-mp="l5p9gapn7t3z">
        <div className="relative overflow-hidden rounded-[26px] bg-white px-5 py-4 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.65)]" data-id="element-360" data-mp="g5b0mqg7wf4w">
          {/* subtle family-role cue behind the product */}
          <AnimatePresence data-id="element-361" data-mp="18t6xbv5se71o">
            {guess && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 0.1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="pointer-events-none absolute -right-4 bottom-0 top-2 flex items-center" data-id="element-362" data-mp="18urs9lw0js02">
                <SiblingFigure role={product.answer} className="h-[150px] w-auto" data-id="element-363" data-mp="u1y8vpdmerq4" />
              </motion.div>}
          </AnimatePresence>

          <div className="relative flex items-center gap-4" data-id="element-364" data-mp="1mrnekg19zfy2">
            <ProductVisual product={product.id} className="h-[176px] w-auto shrink-0" data-id="element-365" data-mp="3h0k5xhs5qu8f" />
            <ul className="flex flex-col gap-2" data-id="element-366" data-mp="3h2wmbqi9owjd">
              {product.traits.map((trait, i) => <motion.li key={trait} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3 + i * 0.1,
              duration: 0.45
            }} className="rounded-full bg-[rgba(1,51,43,0.06)] px-3 py-1.5 font-body text-[12px] leading-tight text-background" data-id="element-367" data-mp="3m1g7vpsy86b7" data-mpi={i}>
                  {trait}
                </motion.li>)}
            </ul>
          </div>
        </div>
      </Reveal>

      <AnimatePresence mode="wait" data-id="element-368" data-mp="2tay930z9cr3l">
        {!guess ? <motion.div key="ask" exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.3
      }} className="mt-6" data-id="element-369" data-mp="2tbhqmgkzxdh3">
            <p className="font-heading text-[19px] text-white" data-id="element-370" data-mp="rjuw451wb0fv">{product.question}</p>
            <div className="mt-3" data-id="element-371" data-mp="2c1drva3y5z2d">
              <SiblingGuessRow guess={guess} onGuess={setGuess} data-id="element-372" data-mp="2c2f77qrzx7pw" />
            </div>
          </motion.div> : <motion.div key="reveal" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-6" data-id="element-373" data-mp="274dnd3mqx4dd">
            <p className="font-body text-[13px] text-white/50" data-id="element-374" data-mp="2urc0psfkjw72">
              {matched ? 'Same instinct.' : 'Interesting call.'} We were thinking...
            </p>
            <div className="mt-2 flex items-center gap-3" data-id="element-375" data-mp="34qazmjaj8dgv">
              <motion.span initial={{
            scale: 0.7,
            rotate: -8
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 16,
            delay: 0.25
          }} className="block shrink-0" data-id="element-376" data-mp="34psvl0hmouye">
                <SiblingFigure role={product.answer} className="h-14 w-14" data-id="element-377" data-mp="15e8ivgtalx6w" />
              </motion.span>
              <h3 className="font-heading text-[28px] leading-[1.02] tracking-[-0.02em] text-primary" data-id="element-378" data-mp="3skl722qwisy3">
                {product.answerLabel}.
              </h3>
            </div>
            <p className="mt-3 font-body text-[15px] leading-relaxed text-white/75" data-id="element-379" data-mp="6dwj8g1pz8fk">
              {product.supporting}
            </p>
            <p className="mt-4 border-t border-white/[0.12] pt-4 font-body text-[13px] leading-relaxed text-white/55" data-id="element-380" data-mp="afhvq0acdkp8">
              {product.closing}
            </p>
            <div className="mt-5" data-id="element-381" data-mp="3etgcvg6aysv5">
              <PillButton tone="dark" full onClick={onNext} data-id="element-382" data-mp="3euhnjjjepdcc">
                {isLast ? 'So, the whole family...' : 'Next sibling'}
              </PillButton>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}